/**
 * Command palette ranking — pure, so it can be tested without a browser.
 *
 * Ordering is the whole feature: typing three letters must put the thing you
 * meant first. Kept out of the component because that file imports the i18n
 * provider, which reaches Vite's env and cannot be loaded by the node runner.
 */

export type CommandKind = 'page' | 'site' | 'ticket' | 'action';

export interface CommandItem {
  id: string;
  kind: CommandKind;
  /** Primary text. */
  label: string;
  /** Secondary text: a property code, a ticket number, a hint. */
  hint?: string;
  /** Extra text matched against the query but not displayed. */
  keywords?: string;
  run: () => void;
}

const MAX_RESULTS = 12;

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Match quality: a prefix beats a word start, which beats a bare substring.
 * Returns null when the item does not match at all.
 */
export function scoreCommand(item: CommandItem, query: string): number | null {
  const q = normalize(query);
  if (!q) return 0;
  const haystack = normalize(`${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`);
  if (!haystack.includes(q)) return null;
  if (haystack.startsWith(q)) return 0;
  if (haystack.split(' ').some((word) => word.startsWith(q))) return 1;
  return 2;
}

/**
 * Kind order. The palette is used to go somewhere far more often than to do
 * something, so a place outranks a record about that place: typing "charlotte"
 * in a portfolio of sixteen hotels means the hotel, even when a ticket's title
 * happens to begin with the same word.
 */
const KIND_RANK: Record<CommandKind, number> = {
  site: 0,
  ticket: 1,
  page: 2,
  action: 3,
};

/**
 * Ordered by kind first, then by match quality, then by the order the caller
 * supplied. Capped, because a palette that scrolls is a list, not a shortcut.
 */
export function filterCommands(items: CommandItem[], query: string): CommandItem[] {
  const scored = items
    .map((item, index) => ({ item, index, score: scoreCommand(item, query) }))
    .filter((row): row is { item: CommandItem; index: number; score: number } => row.score !== null)
    .sort(
      (a, b) =>
        KIND_RANK[a.item.kind] - KIND_RANK[b.item.kind] ||
        a.score - b.score ||
        a.index - b.index,
    );
  return scored.slice(0, MAX_RESULTS).map((row) => row.item);
}
